import { Injectable } from '@angular/core';
import { environment, UrlMs } from '@environment';
import jwtDecode from 'jwt-decode';
import { RequestHandlerService } from '../../../core/services/request-handler/request-handler.service';
import { LastLoginModel } from '../../../features/models/client-summary/client-summary.model';
import { LoginResponseModel } from '../../../features/models/login/login.model';
import { IDatosEjecutivo, IEjecutivoCarteraMapped } from '../../interfaces/cartera-clientes.interface';
import { IDataToken } from '../../interfaces/shared.interface';
import { ExecutiveData, ExecutiveModel } from '../../models/executive.model';
import { LastSessionModel } from '../../models/last-session.model';

@Injectable({
    providedIn: 'root',
})
export class ExecutiveService {
    // Todo: revisar todo esto más adelante
    executiveModel: ExecutiveModel;
    lastLogin: LastLoginModel;
    sessionId: string;

    constructor(private requestHandler: RequestHandlerService) {
        if (sessionStorage.getItem(LocalStorageKeys.DATA_CONTENTFUL)) {
            this.executiveModel = JSON.parse(sessionStorage.getItem(LocalStorageKeys.DATA_CONTENTFUL));
        }

        if (sessionStorage.getItem(LocalStorageKeys.DATA_EXECUTIVE)) {
            this.executiveModel.executiveData = JSON.parse(sessionStorage.getItem(LocalStorageKeys.DATA_EXECUTIVE));
        }

        if (sessionStorage.getItem(LocalStorageKeys.ID_SESSION)) {
            this.sessionId = sessionStorage.getItem(LocalStorageKeys.ID_SESSION);
        }

        this.lastLogin = JSON.parse(sessionStorage.getItem(LocalStorageKeys.LAST_SESSION));
    }

    public setLastSession(lastSession: LastSessionModel) {
        sessionStorage.setItem(LocalStorageKeys.LAST_SESSION, JSON.stringify(lastSession));
        this.lastLogin = lastSession;
    }

    public setLoginData(resp: LoginResponseModel, dataContentful: ExecutiveModel, dataExecutive: ExecutiveData, sessionId) {
        sessionStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, resp.refreshToken);

        sessionStorage.setItem(LocalStorageKeys.DATA_CONTENTFUL, JSON.stringify(dataContentful));
        this.executiveModel = dataContentful;

        sessionStorage.setItem(LocalStorageKeys.DATA_EXECUTIVE, JSON.stringify(dataExecutive));
        this.executiveModel.executiveData = dataExecutive;

        sessionStorage.setItem(LocalStorageKeys.ID_SESSION, sessionId);
        this.sessionId = sessionId;
    }

    public setToken(token: string) {
        sessionStorage.setItem(LocalStorageKeys.TOKEN, token);
    }

    public async getDataExecutive(dni: string): Promise<ExecutiveData> {
        try {
            return await this.requestHandler.doPost<ExecutiveData>(`${environment.ENDPOINTS.BFF.BFF_ASESORES}/ws-ryq/data-executive`, { dni });
        } catch (error) {
          return this.getDatosEjecutivoDefault('0');
        }
    }

    async obtenerDatosEjecutivo(): Promise<IDatosEjecutivo | null> {
        try {
          return await this.requestHandler.doGet<IDatosEjecutivo>(`${UrlMs}/cartera-clientes/obtener-datos-ejecutivo`);
        } catch (error) {
          return null;
        }
    }

    async getDatosEjecutivo(rutAsesor: string): Promise<ExecutiveData> {
      const [rut = '0', dv = ''] = rutAsesor?.split('-');
      const { cuentaAD = '', Nombre = '', PerfilAsesor}: IDataToken= jwtDecode(sessionStorage.getItem(LocalStorageKeys.TOKEN) || '');
      const correo = cuentaAD ? `${cuentaAD}@sura.cl` : '';
        try {
          const { data }: IDatosEjecutivo = await this.obtenerDatosEjecutivo();
          return this.mapDatosEjecutivo(data, { rut, dv, nombreEjecutivo: Nombre, correo, perfilAsesor: PerfilAsesor });
        } catch (error) {
          return this.getDatosEjecutivoDefault(rut, dv, Nombre, { correo, perfilAsesor: PerfilAsesor });
        }
    }

    mapDatosEjecutivo(data: IEjecutivoCarteraMapped, dataAdicional: any): ExecutiveData {
      const { correo = '', dv = '', nombreEjecutivo, rut = '', perfilAsesor } = dataAdicional;
      return {
        RutEjecutivo: data?.rut || rut,
        DVEjecutivo: data?.dv || dv,
        NombreEjecutivo: nombreEjecutivo,
        ApellidoPaternoEjecutivo: data?.apellidoPaterno || '',
        ApellidoMaternoEjecutivo: data?.apellidoMaterno || '',
        TelefonoEjecutivo: data?.telefono || '',
        TelefonoEjecutivoAntiguo: data?.telefono || '',
        MailEjecutivo: data?.email || correo,
        MailEjecutivoAntiguo: data?.email || correo,
        CodigoCanalEjecutivo: '0',
        NombreCanalEjecutivo: '',
        CodigoSucursalEjecutivo: data?.idSucursal || '0',
        NombreSucursalEjecutivo: data?.sucursal || '',
        CodigoRedEjecutivo: data?.codArea || '0',
        CodigoPerfil: '0',
        DescripcionPerfil: perfilAsesor?.nombrePerfil || '',
        CodigoEstadoServicio: '01',
        MensajeEstadoServicio: 'TRANSACCIÓN EXITOSA SIN INFORMACIÓN',
        DetalleEstadoServicio: 'TRANSACCIÓN SIN RETORNO DE DATOS',
      };
    }

    private getDatosEjecutivoDefault(rut = '', dv = '', nombre = '', dataAdicional: any = {}): ExecutiveData {
      const { correo = '', perfilAsesor } = dataAdicional || {};
      return {
        RutEjecutivo: rut,
        DVEjecutivo: dv,
        NombreEjecutivo: nombre,
        ApellidoPaternoEjecutivo: '',
        ApellidoMaternoEjecutivo: '',
        TelefonoEjecutivo: '',
        TelefonoEjecutivoAntiguo: '',
        MailEjecutivo: correo,
        MailEjecutivoAntiguo: correo,
        CodigoCanalEjecutivo: '0',
        NombreCanalEjecutivo: '',
        CodigoSucursalEjecutivo: '0',
        NombreSucursalEjecutivo: '',
        CodigoRedEjecutivo: '',
        CodigoPerfil: '0',
        DescripcionPerfil: perfilAsesor?.nombrePerfil || '',
        CodigoEstadoServicio: '01',
        MensajeEstadoServicio: 'TRANSACCIÓN EXITOSA SIN INFORMACIÓN',
        DetalleEstadoServicio: 'TRANSACCIÓN SIN RETORNO DE DATOS',
      };
    }

    /**
     * Recupera la cartea de asesores segun rut de usuario conectado
     * @param rut
     * @returns
     */
    public async getCarteraExecutive(rut: string): Promise<any> {
        return this.requestHandler.doGet<any>(`${environment.ENDPOINTS.BFF.BFF_ASESORES}/executive-customers/${rut}`);
    }

    getPerfil() {
        return JSON.parse(sessionStorage.getItem('perfilAsesor'));
    }
}

export enum LocalStorageKeys {
    TOKEN = 'token',
    XTOKEN = 'xtoken',
    REFRESH_TOKEN = 'refreshToken',
    ID_SESSION = 'idSesion',
    DATA_CONTENTFUL = 'dataContentful',
    DATA_EXECUTIVE = 'dataExecutive',
    LAST_SESSION = 'lastSession',
    PREVIOUS_URL = 'previousUrl',
}
